import { useState, useEffect, useCallback } from 'react';
import { preparePreApprovalForSubmission, transformPreApprovalToFormData } from '../utils/preApprovalTransformers';
import { useLanguage } from '../../../../../contexts/LanguageContext';


// Status constants
const STATUS = {
    DRAFT: 10,
    WAITING: 20,
    RETURNED: 15,
    ACCEPTED: 30,
    REJECTED: 40
};

// Tab index to status mapping
const TAB_STATUS_MAP = {
    0: STATUS.DRAFT,
    1: STATUS.WAITING,
    2: STATUS.RETURNED,
    3: STATUS.ACCEPTED,
    4: STATUS.REJECTED
};

const INITIAL_STATE = {
    failureDate: null,
    failureKM: '',
    chassisNo: '',
    estimatedPartCost: '',
    estimatedLaborCost: '',
    otherCosts: '',
    totalCosts: '',
    approvalNote: '',
    failureMode: null,
    customerStatement: '',
    failureReason: '',
    resolutionSummary: '',
    existingFileUrl: '',
    partCode: null,
    vehicleItemCode: '',
    mainCategory: '',
    secondCategory: '',
    subCategory: '',
    subPartCategory: '',
    itemCode: null,
    preAppNo: '',
    status: 10
};


/**
 * Custom hook for managing pre-approval form state and operations
 * @param {Function} savePreApproval - Function to save pre-approval data
 * @returns {Object} Form state and operations
 */
const usePreApprovalForm = (savePreApproval) => {

    const [formData, setFormData] = useState(INITIAL_STATE);
    const [submitted, setSubmitted] = useState(false);
    const { th } = useLanguage();


    useEffect(() => {
        setFormData((prev) => {
            const total = (prev.estimatedPartCost || 0) + (prev.estimatedLaborCost || 0) + (prev.otherCosts || 0);
            if (prev.totalCosts === total) return prev;
            return { ...prev, totalCosts: total };
        });
    }, [formData.estimatedPartCost, formData.estimatedLaborCost, formData.otherCosts]);

    /**
     * Handle form field changes
     * @param {string} key - Field name
     * @param {any} value - Field value
     */
    const handleChange = useCallback((key, value) => {
        setFormData((prev) => ({
            ...prev,
            [key]: value
        }));
    }, []);

    /**
     * Reset form to initial state
     */
    const resetForm = useCallback(() => {
        setFormData(INITIAL_STATE);
        setSubmitted(false);
    }, []);

    /**
     * Set form data from pre-approval object
     * @param {Object} preApproval - Pre-approval data
     */
    const setChangeData = useCallback((preApproval) => {
        const transformedData = transformPreApprovalToFormData(preApproval, []);

        setFormData(() => {
            const mainCategoryId = transformedData.mainCategory || null;
            const secondCategoryId = preApproval.secondaryCategory || transformedData.secondCategory || null;
            const subCategoryId = transformedData.subCategory || null;
            const subPartCategoryId = transformedData.subPartCategory || null;

            return {
                ...transformedData,
                failureDate: transformedData.failureDate || null,
                failureKM: transformedData.failureKM || '',
                chassisNo: transformedData.chassisNo || '',
                estimatedPartCost: transformedData.estimatedPartCost || 0,
                estimatedLaborCost: transformedData.estimatedLaborCost || 0,
                otherCosts: transformedData.otherCosts || 0,
                totalCosts: transformedData.totalCosts || 0,
                approvalNote: transformedData.approvalNote || '',
                customerStatement: transformedData.customerStatement || '',
                failureReason: transformedData.failureReason || '',
                resolutionSummary: transformedData.resolutionSummary || '',
                existingFileUrl: transformedData.existingFileUrl || '',
                partCode: transformedData.partCode || null,
                failureMode: transformedData.failureMode || null,
                vehicleItemCode: transformedData.vehicleItemCode || '',
                bosBranchCode: preApproval.bosBranchCode,
                bosBranchSiteCode: preApproval.bosBranchSiteCode,
                preAppNo: preApproval.preAppNo,
                statusDesc: preApproval.statusDesc,
                itemCode: preApproval.itemCode,
                mainCategory: mainCategoryId,
                secondCategory: secondCategoryId,
                subCategory: subCategoryId,
                subPartCategory: subPartCategoryId
            };
        });
    }, []);

    /**
     * Handle form submission
     * @param {number} activeIndex - Active tab index
     * @param {boolean} editMode - Whether this is an edit operation
     * @param {Object} currentPreApproval - Current pre-approval data (for edit operations)
     * @param {Function} hideDialog - Function to hide the dialog
     * @param {Function} setFinalyDescriptionDialogVisible - Function to show/hide finaly description dialog
     * @returns {Promise<boolean>} Success status
     */
    const handleSubmit = useCallback(
        async (activeIndex, editMode, currentPreApproval, hideDialog, setFinalyDescriptionDialogVisible, files = []) => {
            setSubmitted(true);

            if (!formData.chassisNo || !formData.failureDate) {
                return false;
            }

            try {
                const apiData = preparePreApprovalForSubmission(formData, editMode, currentPreApproval);
                const result = await savePreApproval(apiData, editMode, currentPreApproval, false, files);

                if (result.success) {
                    const currentStatus = TAB_STATUS_MAP[activeIndex];

                    if (editMode && currentStatus === STATUS.RETURNED) {
                        setFinalyDescriptionDialogVisible(false);
                    } else if (activeIndex === 1 || activeIndex === 0 || !editMode) {
                        setFinalyDescriptionDialogVisible(false);
                    } else {
                        setFinalyDescriptionDialogVisible(true);
                    }

                    hideDialog();
                    resetForm();
                }

                return result.success;
            } catch (err) {
                console.error('Form submission error:', err);
                return false;
            }
        },
        [formData, savePreApproval, resetForm]
    );

    /**
     * Render dialog footer with action buttons
     * @param {number} activeIndex - Active tab index
     * @param {Function} handleSubmit - Function to handle form submission
     * @returns {JSX.Element} Footer component
     */
    const renderDialogFooter = useCallback((activeIndex, handleSubmitFn) => {
        return (
            <div className="flex justify-content-end gap-2">
                {activeIndex === 1 || activeIndex === 0 ? (
                    <>
                        <button className="p-button p-component" onClick={handleSubmitFn}>
                            <span className="p-button-icon p-button-icon-left pi pi-check"></span>
                            <span className="p-button-label">{th('preApproval.buttons.sendForApproval')}</span>
                        </button>
                        <button className="p-button p-component" onClick={handleSubmitFn}>
                            <span className="p-button-icon p-button-icon-left pi pi-check"></span>
                            <span className="p-button-label">{th('preApproval.buttons.saveAndClose')}</span>
                        </button>
                    </>
                ) : (
                    <>
                        <button className="p-button p-component" onClick={handleSubmitFn}>
                            <span className="p-button-icon p-button-icon-left pi pi-check"></span>
                            <span className="p-button-label">{th('preApproval.buttons.approve')}</span>
                        </button>
                        <button className="p-button p-component" onClick={handleSubmitFn}>
                            <span className="p-button-icon p-button-icon-left pi pi-check"></span>
                            <span className="p-button-label">{th('preApproval.buttons.reject')}</span>
                        </button>
                        <button className="p-button p-component" onClick={handleSubmitFn}>
                            <span className="p-button-icon p-button-icon-left pi pi-check"></span>
                            <span className="p-button-label">{th('preApproval.buttons.return')}</span>
                        </button>
                    </>
                )}
                {activeIndex === 2 && (
                    <button className="p-button p-component" onClick={handleSubmitFn}>
                        <span className="p-button-icon p-button-icon-left pi pi-check"></span>
                        <span className="p-button-label">{th('preApproval.buttons.sendForApproval')}</span>
                    </button>
                )}
            </div>
        );
    }, []);

    return {
        formData,
        submitted,
        handleChange,
        resetForm,
        setChangeData,
        handleSubmit,
        renderDialogFooter
    };
};

export default usePreApprovalForm;
